import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type Size = "default" | "sm" | "lg";
type Tone = "spectrum" | "violet" | "aurora";

const sizeClasses: Record<Size, string> = {
  default: "h-9 px-4 has-[>svg]:px-3",
  sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
  lg: "h-11 px-6 has-[>svg]:px-4",
};

/*─────────────────────────────────────────────────────────────
  FoilButton — holographic foil effect
  Adapted from the Monarch Foil palette, tuned for dark theme.
  Cyan → purple → white → gold → magenta → cyan spectrum.
  ─────────────────────────────────────────────────────────── */
const FOIL = {
  bg: "oklch(0.22 0.02 280)",
  text: "oklch(0.95 0.01 280)",
  gradient: `linear-gradient(
    135deg,
    hsl(192, 97%, 60%) 0%,
    hsl(281, 62%, 58%) 20%,
    hsl(0, 0%, 95%) 40%,
    hsl(54, 87%, 63%) 55%,
    hsl(340, 81%, 59%) 75%,
    hsl(192, 97%, 60%) 100%
  )`,
  radial: `radial-gradient(
    farthest-corner circle at var(--foil-x) var(--foil-y),
    hsla(180, 100%, 89%, 0.5) 5%,
    hsla(260, 30%, 70%, 0.3) 40%,
    hsl(0, 0%, 0%) 130%
  )`,
};

const VIOLET_FOIL = {
  bg: "color-mix(in srgb, oklch(0.34 0.18 275) 24%, transparent)",
  text: "oklch(0.97 0.02 285)",
  gradient: `linear-gradient(
    135deg,
    hsl(245, 96%, 60%) 0%,
    hsl(259, 94%, 62%) 26%,
    hsl(278, 82%, 65%) 58%,
    hsl(315, 76%, 72%) 82%,
    hsl(190, 92%, 72%) 100%
  )`,
  radial: `radial-gradient(
    farthest-corner circle at var(--foil-x) var(--foil-y),
    hsla(202, 100%, 91%, 0.5) 5%,
    hsla(275, 92%, 72%, 0.34) 42%,
    hsl(245, 62%, 28%) 130%
  )`,
};

const AURORA_FOIL = {
  bg: "linear-gradient(105deg, rgba(13, 32, 40, 0.82) 0%, rgba(15, 20, 36, 0.78) 52%, rgba(31, 16, 43, 0.8) 100%)",
  text: "oklch(0.96 0.015 272)",
  gradient: `linear-gradient(
    100deg,
    hsl(188, 82%, 79%) 0%,
    hsl(222, 90%, 83%) 52%,
    hsl(275, 83%, 72%) 100%
  )`,
  radial: `radial-gradient(
    farthest-corner circle at var(--foil-x) var(--foil-y),
    hsla(185, 100%, 96%, 0.55) 4%,
    hsla(260, 100%, 88%, 0.25) 44%,
    hsla(275, 74%, 62%, 0.12) 130%
  )`,
};

interface FoilButtonProps {
  children: ReactNode;
  className?: string;
  size?: Size;
  tone?: Tone;
  idle?: boolean;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  onClick?: () => void;
}

export function FoilButton({
  children,
  className,
  size = "lg",
  tone = "spectrum",
  idle = false,
  type = "button",
  disabled = false,
  onClick,
}: FoilButtonProps) {
  const foil =
    tone === "violet" ? VIOLET_FOIL : tone === "aurora" ? AURORA_FOIL : FOIL;
  const rootRef = useRef<HTMLButtonElement>(null);
  const rafId = useRef<number | null>(null);
  const idleRaf = useRef<number | null>(null);
  const isHovering = useRef(false);
  const idleStart = useRef(0);

  const setVars = useCallback(
    (xPct: number, yPct: number, glow: number, hovered: boolean) => {
      const el = rootRef.current;
      if (!el) return;
      const centerX = xPct - 50;
      const centerY = yPct - 50;
      const rotateX = (centerY / 50) * -10;
      const rotateY = (centerX / 50) * 10;

      el.style.setProperty("--foil-x", `${xPct}%`);
      el.style.setProperty("--foil-y", `${yPct}%`);
      el.style.setProperty("--foil-rotate-x", `${rotateX}deg`);
      el.style.setProperty("--foil-rotate-y", `${rotateY}deg`);
      el.style.setProperty("--foil-opacity", `${glow}`);
      el.style.setProperty("--foil-glare-opacity", `${glow}`);
      el.style.setProperty(
        "--foil-transition",
        hovered ? "none" : "transform 0.2s ease",
      );
      el.style.setProperty(
        "--foil-scale",
        hovered ? "translateZ(6px) scale(1.01)" : "translateZ(0) scale(1)",
      );
    },
    [],
  );

  const softClamp = (v: number, min: number, max: number, s: number) => {
    if (v < min) return min - s * (1 - Math.exp((v - min) / s));
    if (v > max) return max + s * (1 - Math.exp(-(v - max) / s));
    return v;
  };

  const startIdle = useCallback(() => {
    if (!idle || idleRaf.current) return;
    idleStart.current = performance.now();
    const loop = (now: number) => {
      if (isHovering.current) {
        idleRaf.current = null;
        return;
      }
      const t = (now - idleStart.current) / 1000;
      const phase = (t / 6) % 1;
      const ping = phase < 0.5 ? phase * 2 : 2 - phase * 2;
      const eased = ping * ping * ping * (ping * (ping * 6 - 15) + 10);
      const sweep = eased * 2 - 1;
      const flash = Math.pow(Math.max(0, 1 - Math.abs(phase - 0.22) / 0.15), 8);
      setVars(
        softClamp(50 + sweep * 30, 0, 100, 12),
        softClamp(50 + sweep * 20, 0, 100, 12),
        0.12 + 0.25 * flash,
        false,
      );
      idleRaf.current = requestAnimationFrame(loop);
    };
    idleRaf.current = requestAnimationFrame(loop);
  }, [setVars, idle]);

  useEffect(() => {
    if (idle) startIdle();
    return () => {
      if (idleRaf.current) {
        cancelAnimationFrame(idleRaf.current);
        idleRaf.current = null;
      }
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [startIdle, idle]);

  const onMouseMove = (e: React.MouseEvent) => {
    isHovering.current = true;
    if (idleRaf.current) {
      cancelAnimationFrame(idleRaf.current);
      idleRaf.current = null;
    }
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const xPct = Math.max(
      0,
      Math.min(100, ((e.clientX - rect.left) / rect.width) * 100),
    );
    const yPct = Math.max(
      0,
      Math.min(100, ((e.clientY - rect.top) / rect.height) * 100),
    );
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      setVars(xPct, yPct, tone === "aurora" ? 0 : 0.65, true);
    });
  };

  const onMouseLeave = () => {
    isHovering.current = false;
    if (idle) {
      startIdle();
    } else {
      setVars(50, 50, 0, false);
    }
  };

  return (
    <button
      ref={rootRef}
      disabled={disabled}
      type={type}
      className={cn(
        "relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 overflow-hidden",
        "rounded-md text-sm font-medium whitespace-nowrap",
        "disabled:pointer-events-none disabled:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        sizeClasses[size],
        className,
      )}
      style={
        {
          "--foil-x": "50%",
          "--foil-y": "50%",
          "--foil-rotate-x": "0deg",
          "--foil-rotate-y": "0deg",
          "--foil-opacity": "0",
          "--foil-glare-opacity": "0",
          "--foil-transition": "transform 0.2s ease",
          "--foil-scale": "translateZ(0) scale(1)",
          perspective: "1000px",
          transformStyle: "preserve-3d",
          background: foil.bg,
          color: foil.text,
          transform:
            "rotateX(var(--foil-rotate-x)) rotateY(var(--foil-rotate-y)) var(--foil-scale)",
          transition: "var(--foil-transition)",
          contain: "paint",
          willChange: "transform",
        } as React.CSSProperties
      }
      onClick={onClick}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
    >
      <span className="relative z-10 flex items-center gap-2">{children}</span>
      <span
        className="pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{
          backgroundImage: `${foil.gradient}, ${foil.radial}`,
          backgroundSize: "400% 400%",
          backgroundPosition: "var(--foil-x) var(--foil-y)",
          backgroundBlendMode: "normal",
          mixBlendMode: "color-dodge",
          opacity: "var(--foil-opacity)",
          transition: "opacity 0.3s ease",
        }}
      />
      <span
        className="pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{
          background: `radial-gradient(farthest-corner circle at var(--foil-x) var(--foil-y), hsla(204, 100%, 95%, 0.8) 5%, hsla(250, 15%, 20%, 1) 150%)`,
          mixBlendMode: "overlay",
          opacity: "var(--foil-glare-opacity)",
          transition: "opacity 0.3s ease",
        }}
      />
    </button>
  );
}
